import { useEffect, useState } from 'react'
import {
  Search, Plus, Trash2, Check, X, Clock, Users,
  BookOpen, Award
} from 'lucide-react'

interface Student {
  _id: string
  name: string
  email: string
  phone?: string
  avatar?: string
}

interface Course {
  _id: string
  title: string
  description: string
  category: {
    _id: string
    name: string
    color: string
  } | string
  price: number
  instructor: string
  duration: string
  level: string
  thumbnail?: string
}

interface LiveSession {
  _id: string
  title: string
  description: string
  scheduledAt: string
  duration: number
  cost: number
  maxParticipants: number
  category: {
    _id: string
    name: string
    color: string
  } | string
  registeredCount: number
  status: string
  registrationDeadline?: string
  isFull?: boolean
}

interface CourseAssignment {
  id: string
  courseId: string
  courseName: string
  coursePrice: number
  studentId: string
  studentName: string
  studentEmail: string
  assignedDate: string
  assignedBy: string
  notes?: string
  paymentStatus: 'free' | 'paid_offline' | 'waived'
  paymentAmount?: number
  paymentNotes?: string
}

interface LiveSessionAssignment {
  id: string
  liveSessionId: string
  sessionName: string
  sessionDate: string
  sessionCost: number
  studentId: string
  studentName: string
  studentEmail: string
  assignedDate: string
  assignedBy: string
  notes?: string
  paymentStatus: 'free' | 'paid_offline' | 'waived'
  paymentAmount?: number
  paymentNotes?: string
  attended: boolean
}

export default function AssignCourse() {
  const [students, setStudents] = useState<Student[]>([])
  const [courses, setCourses] = useState<Course[]>([])
  const [liveSessions, setLiveSessions] = useState<LiveSession[]>([])
  const [courseAssignments, setCourseAssignments] = useState<CourseAssignment[]>([])
  const [sessionAssignments, setSessionAssignments] = useState<LiveSessionAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'courses' | 'sessions'>('courses')
  const [searchStudent, setSearchStudent] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'course' | 'session'>('course')
  const [filter, setFilter] = useState<'all' | 'free' | 'paid_offline' | 'waived'>('all')
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<string[]>([])
  const [enrolledSessionIds, setEnrolledSessionIds] = useState<string[]>([])

  // Form states
  const [formData, setFormData] = useState({
    studentId: '',
    courseId: '',
    liveSessionId: '',
    paymentStatus: 'free' as 'free' | 'paid_offline' | 'waived',
    paymentAmount: 0,
    notes: '',
    paymentNotes: ''
  })

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    fetchInitialData()
  }, [])

  // Fetch enrolled courses when student is selected
  useEffect(() => {
    if (formData.studentId) {
      fetchStudentEnrollments(formData.studentId)
    } else {
      setEnrolledCourseIds([])
      setEnrolledSessionIds([])
    }
  }, [formData.studentId])

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token')
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      // Fetch students
      const studentsRes = await fetch('/api/admin/users?role=USER', { headers })
      if (studentsRes.ok) {
        const data = await studentsRes.json()
        setStudents(data.users || [])
      }

      // Fetch courses
      const coursesRes = await fetch('/api/admin/courses', { headers })
      if (coursesRes.ok) {
        const data = await coursesRes.json()
        setCourses(data.courses || [])
      }

      // Fetch live sessions for assignment
      const sessionsRes = await fetch('/api/admin/live-sessions-for-assign', { headers })
      if (sessionsRes.ok) {
        const data = await sessionsRes.json()
        setLiveSessions(data.sessions || [])
      }

      // Fetch assignments
      await Promise.all([
        fetchCourseAssignments(headers),
        fetchSessionAssignments(headers)
      ])
    } catch (error) {
      console.error('Error fetching initial data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCourseAssignments = async (headers?: any) => {
    try {
      const token = localStorage.getItem('token')
      const finalHeaders = headers || {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      const response = await fetch('/api/admin/course-assignments', {
        headers: finalHeaders
      })

      if (response.ok) {
        const data = await response.json()
        setCourseAssignments(data.assignments || [])
      }
    } catch (error) {
      console.error('Error fetching course assignments:', error)
    }
  }

  const fetchSessionAssignments = async (headers?: any) => {
    try {
      const token = localStorage.getItem('token')
      const finalHeaders = headers || {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      const response = await fetch('/api/admin/live-session-assignments', {
        headers: finalHeaders
      })

      if (response.ok) {
        const data = await response.json()
        setSessionAssignments(data.assignments || [])
      }
    } catch (error) {
      console.error('Error fetching session assignments:', error)
    }
  }

  const fetchStudentEnrollments = async (studentId: string) => {
    try {
      const token = localStorage.getItem('token')
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      // Fetch student's course enrollments
      const enrollmentsRes = await fetch(`/api/admin/users/${studentId}/enrollments`, { headers })
      if (enrollmentsRes.ok) {
        const data = await enrollmentsRes.json()
        const courseIds = data.enrollments?.map((e: any) => e.course?._id || e.course).filter(Boolean) || []
        setEnrolledCourseIds(courseIds)
      }

      // Fetch student's live session registrations
      const registrationsRes = await fetch(`/api/admin/users/${studentId}/live-session-registrations`, { headers })
      if (registrationsRes.ok) {
        const data = await registrationsRes.json()
        const sessionIds = data.registrations?.map((r: any) => r.liveSession?._id || r.liveSession).filter(Boolean) || []
        setEnrolledSessionIds(sessionIds)
      }
    } catch (error) {
      console.error('Error fetching student enrollments:', error)
      // If API doesn't exist, use course assignments as fallback
      const studentCourses = courseAssignments
        .filter(a => a.studentId === studentId)
        .map(a => a.courseId)
      setEnrolledCourseIds(studentCourses)

      const studentSessions = sessionAssignments
        .filter(a => a.studentId === studentId)
        .map(a => a.liveSessionId)
      setEnrolledSessionIds(studentSessions)
    }
  }

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    if (!formData.studentId) errors.studentId = 'Please select a student'
    if (modalType === 'course' && !formData.courseId) errors.courseId = 'Please select a course'
    if (modalType === 'session' && !formData.liveSessionId) errors.liveSessionId = 'Please select a live session'
    if (formData.paymentStatus === 'paid_offline' && formData.paymentAmount <= 0) {
      errors.paymentAmount = 'Payment amount is required for offline payments'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAssign = async () => {
    if (!validateForm()) return

    try {
      const token = localStorage.getItem('token')
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
      const isSession = modalType === 'session'

      const url = isSession ? '/api/admin/assign-live-session' : '/api/admin/assign-course'
      const body = isSession ? {
        studentId: formData.studentId,
        liveSessionId: formData.liveSessionId,
        paymentStatus: formData.paymentStatus,
        paymentAmount: formData.paymentStatus === 'paid_offline' ? formData.paymentAmount : 0,
        notes: formData.notes,
        paymentNotes: formData.paymentNotes,
        assignedBy: currentUser.name || 'Admin'
      } : {
        studentId: formData.studentId,
        courseId: formData.courseId,
        paymentStatus: formData.paymentStatus,
        paymentAmount: formData.paymentStatus === 'paid_offline' ? formData.paymentAmount : 0,
        notes: formData.notes,
        paymentNotes: formData.paymentNotes,
        assignedBy: currentUser.name || 'Admin'
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      if (response.ok) {
        alert(`${isSession ? 'Live session' : 'Course'} assigned successfully!`)
        setShowModal(false)
        setFormData({
          studentId: '',
          courseId: '',
          liveSessionId: '',
          paymentStatus: 'free',
          paymentAmount: 0,
          notes: '',
          paymentNotes: ''
        })

        if (isSession) {
          await fetchSessionAssignments()
        } else {
          await fetchCourseAssignments()
        }
      } else {
        const error = await response.json()
        alert(`Failed to assign: ${error.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error assigning:', error)
      alert('Error during assignment')
    }
  }

  const handleRemoveAssignment = async (assignmentId: string, isSession: boolean = false) => {
    if (!confirm(`Are you sure you want to remove this ${isSession ? 'session' : 'course'} assignment?`)) return

    try {
      const token = localStorage.getItem('token')
      const endpoint = isSession ? `live-session-assignments/${assignmentId}` : `course-assignments/${assignmentId}`

      const response = await fetch(`/api/admin/${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        if (isSession) {
          setSessionAssignments(sessionAssignments.filter(a => a.id !== assignmentId))
        } else {
          setCourseAssignments(courseAssignments.filter(a => a.id !== assignmentId))
        }
        alert('Assignment removed successfully')
      } else {
        alert('Failed to remove assignment')
      }
    } catch (error) {
      console.error('Error removing assignment:', error)
      alert('Error removing assignment')
    }
  }

  const getSelectedStudent = () => students.find(s => s._id === formData.studentId)
  const getSelectedCourse = () => courses.find(c => c._id === formData.courseId)
  const getSelectedSession = () => liveSessions.find(s => s._id === formData.liveSessionId)

  // Filter out already enrolled courses and sessions
  const getAvailableCourses = () => {
    return courses.filter(course => !enrolledCourseIds.includes(course._id))
  }

  const getAvailableSessions = () => {
    const now = new Date()
    return liveSessions.filter(session => {
      const isEnrolled = enrolledSessionIds.includes(session._id)
      const deadlinePassed = session.registrationDeadline && new Date(session.registrationDeadline) < now
      return !isEnrolled && !deadlinePassed
    })
  }

  const filteredCourseAssignments = courseAssignments.filter(a => {
    const matchesFilter = filter === 'all' || a.paymentStatus === filter
    const matchesSearch = searchStudent === '' ||
      a.studentName.toLowerCase().includes(searchStudent.toLowerCase()) ||
      a.studentEmail.toLowerCase().includes(searchStudent.toLowerCase()) ||
      a.courseName.toLowerCase().includes(searchStudent.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const filteredSessionAssignments = sessionAssignments.filter(a => {
    const matchesFilter = filter === 'all' || a.paymentStatus === filter
    const matchesSearch = searchStudent === '' ||
      a.studentName.toLowerCase().includes(searchStudent.toLowerCase()) ||
      a.studentEmail.toLowerCase().includes(searchStudent.toLowerCase()) ||
      a.sessionName.toLowerCase().includes(searchStudent.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const courseStats = {
    total: courseAssignments.length,
    free: courseAssignments.filter(a => a.paymentStatus === 'free').length,
    paidOffline: courseAssignments.filter(a => a.paymentStatus === 'paid_offline').length,
    waived: courseAssignments.filter(a => a.paymentStatus === 'waived').length,
    totalOfflineRevenue: courseAssignments
      .filter(a => a.paymentStatus === 'paid_offline')
      .reduce((sum, a) => sum + (a.paymentAmount || 0), 0)
  }

  const sessionStats = {
    total: sessionAssignments.length,
    free: sessionAssignments.filter(a => a.paymentStatus === 'free').length,
    paidOffline: sessionAssignments.filter(a => a.paymentStatus === 'paid_offline').length,
    waived: sessionAssignments.filter(a => a.paymentStatus === 'waived').length,
    totalOfflineRevenue: sessionAssignments
      .filter(a => a.paymentStatus === 'paid_offline')
      .reduce((sum, a) => sum + (a.paymentAmount || 0), 0),
    attended: sessionAssignments.filter(a => a.attended).length
  }

  const stats = activeTab === 'courses' ? courseStats : sessionStats

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-charcoal-900 dark:text-white mb-2">
            Assign Courses & Live Sessions
          </h1>
          <p className="text-charcoal-600 dark:text-charcoal-400">
            Manually assign courses and live sessions to students
          </p>
        </div>
        <button
          onClick={() => {
            setShowModal(true)
            setModalType(activeTab === 'courses' ? 'course' : 'session')
            setFormData({
              studentId: '',
              courseId: '',
              liveSessionId: '',
              paymentStatus: 'free',
              paymentAmount: 0,
              notes: '',
              paymentNotes: ''
            })
          }}
          className="flex items-center gap-2 px-6 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30 font-semibold"
        >
          <Plus className="h-5 w-5" />
          <span>Assign {activeTab === 'courses' ? 'Course' : 'Session'}</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-2 border border-gray-100 dark:border-dark-700 inline-flex shadow-sm">
        <button
          onClick={() => setActiveTab('courses')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-2xl font-medium transition-all ${activeTab === 'courses'
            ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-400 shadow-sm'
            : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-gray-50 dark:hover:bg-dark-700'
            }`}
        >
          <BookOpen className="h-4 w-4" />
          Courses
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-2xl font-medium transition-all ${activeTab === 'sessions'
            ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-400 shadow-sm'
            : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-gray-50 dark:hover:bg-dark-700'
            }`}
        >
          <Clock className="h-4 w-4" />
          Live Sessions
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.total}</p>
              <p className="text-xs font-medium text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wide">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-50 dark:bg-green-900/20 rounded-2xl">
              <Award className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.free}</p>
              <p className="text-xs font-medium text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wide">Free</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
              <Users className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.paidOffline}</p>
              <p className="text-xs font-medium text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wide">Paid</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-orange-50 dark:bg-orange-900/20 rounded-2xl">
              <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.waived}</p>
              <p className="text-xs font-medium text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wide">Waived</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all col-span-2 md:col-span-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-rose-50 dark:bg-rose-900/20 rounded-2xl">
              <Award className="h-5 w-5 text-rose-600 dark:text-rose-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-charcoal-900 dark:text-white">₹{stats.totalOfflineRevenue.toLocaleString()}</p>
              <p className="text-xs font-medium text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wide">Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-4 border border-gray-100 dark:border-dark-700 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
            <input
              type="text"
              placeholder="Search assignments..."
              value={searchStudent}
              onChange={(e) => setSearchStudent(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
          >
            <option value="all">All Types</option>
            <option value="free">Free</option>
            <option value="paid_offline">Paid Offline</option>
            <option value="waived">Waived</option>
          </select>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl border border-gray-100 dark:border-dark-700 overflow-hidden shadow-sm">
        {(activeTab === 'courses' ? filteredCourseAssignments : filteredSessionAssignments).length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-50 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-8 w-8 text-charcoal-400" />
            </div>
            <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2">
              No assignments found
            </h3>
            <p className="text-charcoal-500 dark:text-charcoal-400">
              {searchStudent ? 'Try adjusting your search query' : 'Start by assigning items to students'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-700/50 border-b border-gray-100 dark:border-dark-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">
                    {activeTab === 'courses' ? 'Course' : 'Live Session'}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Amount</th>
                  {activeTab === 'sessions' && (
                    <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Attended</th>
                  )}
                  <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Assigned Date</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {(activeTab === 'courses' ? filteredCourseAssignments : filteredSessionAssignments).map((assignment: any) => (
                  <tr key={assignment.id} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-bold text-charcoal-900 dark:text-white">{assignment.studentName}</p>
                        <p className="text-sm text-charcoal-500 dark:text-charcoal-400">{assignment.studentEmail}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-charcoal-900 dark:text-white">
                        {activeTab === 'courses' ? assignment.courseName : assignment.sessionName}
                      </p>
                      {activeTab === 'sessions' && (
                        <p className="text-xs text-charcoal-500 dark:text-charcoal-400 mt-0.5">
                          {new Date(assignment.sessionDate).toLocaleDateString()} • {new Date(assignment.sessionDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-bold rounded-full whitespace-nowrap ${assignment.paymentStatus === 'free'
                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : assignment.paymentStatus === 'paid_offline'
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                          : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                        }`}>
                        {assignment.paymentStatus === 'free' ? 'Free' : assignment.paymentStatus === 'paid_offline' ? 'Paid (Offline)' : 'Waived'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-charcoal-900 dark:text-white font-semibold">
                        {assignment.paymentAmount ? `₹${assignment.paymentAmount.toLocaleString()}` : '—'}
                      </span>
                    </td>
                    {activeTab === 'sessions' && (
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${assignment.attended
                          ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                          : 'bg-gray-100 text-charcoal-600 dark:bg-dark-700 dark:text-charcoal-400'
                          }`}>
                          {assignment.attended ? 'Yes' : 'No'}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <span className="text-sm text-charcoal-600 dark:text-charcoal-400">
                        {new Date(assignment.assignedDate).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleRemoveAssignment(assignment.id, activeTab === 'sessions')}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                        title="Remove Assignment"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-charcoal-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white dark:bg-dark-800 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-up border border-gray-100 dark:border-dark-700">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700 sticky top-0 bg-white dark:bg-dark-800 z-10">
              <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white">
                Assign {modalType === 'course' ? 'Course' : 'Live Session'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl transition-colors"
              >
                <X className="h-6 w-6 text-charcoal-500 dark:text-charcoal-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Student Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-charcoal-900 dark:text-white">
                  Select Student
                </label>
                <select
                  value={formData.studentId}
                  onChange={(e) => {
                    setFormData({ ...formData, studentId: e.target.value })
                    setValidationErrors({ ...validationErrors, studentId: '' })
                  }}
                  className={`w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all ${validationErrors.studentId
                    ? 'border-red-500'
                    : 'border-gray-200 dark:border-dark-600'
                    }`}
                >
                  <option value="">Choose a student...</option>
                  {students.map(s => (
                    <option key={s._id} value={s._id}>
                      {s.name} ({s.email})
                    </option>
                  ))}
                </select>
                {validationErrors.studentId && (
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium">{validationErrors.studentId}</p>
                )}
              </div>

              {/* Course/Session Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-charcoal-900 dark:text-white">
                  Select {modalType === 'course' ? 'Course' : 'Live Session'}
                </label>
                <select
                  value={modalType === 'course' ? formData.courseId : formData.liveSessionId}
                  onChange={(e) => {
                    if (modalType === 'course') {
                      setFormData({ ...formData, courseId: e.target.value })
                      setValidationErrors({ ...validationErrors, courseId: '' })
                    } else {
                      setFormData({ ...formData, liveSessionId: e.target.value })
                      setValidationErrors({ ...validationErrors, liveSessionId: '' })
                    }
                  }}
                  className={`w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all ${(modalType === 'course' ? validationErrors.courseId : validationErrors.liveSessionId)
                    ? 'border-red-500'
                    : 'border-gray-200 dark:border-dark-600'
                    }`}
                  disabled={!formData.studentId}
                >
                  <option value="">
                    {!formData.studentId
                      ? 'Select a student first...'
                      : `Choose a ${modalType === 'course' ? 'course' : 'live session'}...`
                    }
                  </option>
                  {(modalType === 'course' ? getAvailableCourses() : getAvailableSessions()).map((item: any) => (
                    <option key={item._id} value={item._id}>
                      {modalType === 'course'
                        ? `${(item as Course).title} (₹${(item as Course).price?.toLocaleString()})`
                        : `${(item as LiveSession).title} - ${new Date((item as LiveSession).scheduledAt).toLocaleDateString()} (₹${(item as LiveSession).cost})`
                      }
                    </option>
                  ))}
                </select>
                {(modalType === 'course' ? validationErrors.courseId : validationErrors.liveSessionId) && (
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium">
                    {modalType === 'course' ? validationErrors.courseId : validationErrors.liveSessionId}
                  </p>
                )}
                {formData.studentId && (modalType === 'course' ? getAvailableCourses() : getAvailableSessions()).length === 0 && (
                  <p className="text-amber-600 dark:text-amber-400 text-sm font-medium">
                    This student has already been assigned all available {modalType === 'course' ? 'courses' : 'live sessions'}.
                  </p>
                )}
              </div>

              {/* Payment Status */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-charcoal-900 dark:text-white">
                  Payment Status
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: 'free', label: 'Free Access', desc: 'Student gets free access' },
                    { value: 'paid_offline', label: 'Paid (Offline)', desc: 'Student paid offline' },
                    { value: 'waived', label: 'Waived', desc: 'Fee waived' }
                  ].map(option => (
                    <label key={option.value} className={`flex flex-col p-4 border rounded-xl cursor-pointer transition-all ${formData.paymentStatus === option.value
                      ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20'
                      : 'border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700/50'
                      }`}>
                      <div className="flex items-center gap-3 mb-1">
                        <input
                          type="radio"
                          name="paymentStatus"
                          value={option.value}
                          checked={formData.paymentStatus === option.value}
                          onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value as any })}
                          className="w-4 h-4 text-rose-600 focus:ring-rose-500 border-gray-300"
                        />
                        <span className={`font-bold ${formData.paymentStatus === option.value ? 'text-rose-700 dark:text-rose-400' : 'text-charcoal-900 dark:text-white'}`}>
                          {option.label}
                        </span>
                      </div>
                      <span className="text-xs text-charcoal-500 dark:text-charcoal-400 ml-7">{option.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Payment Amount */}
              {formData.paymentStatus === 'paid_offline' && (
                <div className="space-y-2 animate-slide-down">
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white">
                    Payment Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.paymentAmount}
                    onChange={(e) => {
                      setFormData({ ...formData, paymentAmount: parseFloat(e.target.value) })
                      setValidationErrors({ ...validationErrors, paymentAmount: '' })
                    }}
                    placeholder="Enter payment amount"
                    className={`w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all ${validationErrors.paymentAmount
                      ? 'border-red-500'
                      : 'border-gray-200 dark:border-dark-600'
                      }`}
                  />
                  {validationErrors.paymentAmount && (
                    <p className="text-red-600 dark:text-red-400 text-sm font-medium">{validationErrors.paymentAmount}</p>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-charcoal-900 dark:text-white">
                  Admin Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add internal notes about this assignment..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all resize-none"
                />
              </div>

              {/* Payment Notes */}
              {formData.paymentStatus === 'paid_offline' && (
                <div className="space-y-2 animate-slide-down">
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white">
                    Payment Details
                  </label>
                  <textarea
                    value={formData.paymentNotes}
                    onChange={(e) => setFormData({ ...formData, paymentNotes: e.target.value })}
                    placeholder="Payment method, transaction ID, payment date, etc."
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all resize-none"
                  />
                </div>
              )}

              {/* Details Box */}
              {(getSelectedCourse() || getSelectedSession()) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-2xl p-5">
                  <p className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    {modalType === 'course' ? 'Course' : 'Session'} Details
                  </p>
                  <div className="space-y-2 text-sm text-blue-700 dark:text-blue-400">
                    {modalType === 'course' ? (
                      <>
                        <p><span className="font-semibold">Title:</span> {getSelectedCourse()?.title}</p>
                        <p><span className="font-semibold">Price:</span> ₹{getSelectedCourse()?.price.toLocaleString()}</p>
                        <p><span className="font-semibold">Level:</span> {getSelectedCourse()?.level}</p>
                        <p><span className="font-semibold">Category:</span> {
                          (() => {
                            const cat = getSelectedCourse()?.category;
                            return typeof cat === 'object' && cat !== null ? cat.name : cat;
                          })()
                        }</p>
                      </>
                    ) : (
                      <>
                        <p><span className="font-semibold">Title:</span> {getSelectedSession()?.title}</p>
                        <p><span className="font-semibold">Date:</span> {new Date(getSelectedSession()?.scheduledAt || '').toLocaleDateString()}</p>
                        <p><span className="font-semibold">Cost:</span> ₹{getSelectedSession()?.cost.toLocaleString()}</p>
                        <p><span className="font-semibold">Category:</span> {
                          (() => {
                            const cat = getSelectedSession()?.category;
                            return typeof cat === 'object' && cat !== null ? cat.name : cat;
                          })()
                        }</p>
                        <p><span className="font-semibold">Registered:</span> {getSelectedSession()?.registeredCount} students</p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex gap-4 p-6 border-t border-gray-100 dark:border-dark-700 bg-gray-50 dark:bg-dark-800/50 sticky bottom-0 rounded-b-3xl">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-6 py-3 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-white dark:hover:bg-dark-700 transition-colors font-bold text-charcoal-600 dark:text-charcoal-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                className="flex-1 px-6 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30 font-bold flex items-center justify-center gap-2"
              >
                <Check className="h-5 w-5" />
                Assign {modalType === 'course' ? 'Course' : 'Session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
